import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeftIcon,
  FolderIcon
} from '@heroicons/react/24/outline'
import { CREATE_CATEGORY } from '../graphql/product-mutations'
import { GET_CATEGORIES } from '../graphql/product-queries'
import { GET_PLATFORMS } from '../graphql/platform-queries'
import { CreateCategoryData, Platform, PlatformAvailability } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import PlatformPicker from '../components/PlatformPicker'
import toast from 'react-hot-toast'

const CreateCategoryPage = () => {
  const navigate = useNavigate()
  // removed unused client
  
  const { data: categoriesData, loading: categoriesLoading } = useQuery(GET_CATEGORIES, {
    variables: { filters: {} }
  })

  const { data: platformsData, loading: platformsLoading } = useQuery<{ platforms: Platform[] }>(
    GET_PLATFORMS,
    { variables: { activeOnly: true } }
  )

  const categories = categoriesData?.categories || []
  const platforms = platformsData?.platforms || []
  const [enabledSlugs, setEnabledSlugs] = useState<Set<string>>(new Set())
  const [formData, setFormData] = useState<CreateCategoryData>({
    name: '',
    parent_id: undefined,
    active: true,
    display_in_pos: true,
    display_in_webshop: true,
  })

  const availabilities: PlatformAvailability[] = platforms.map(p => ({
    platform: p,
    enabled: enabledSlugs.has(p.slug),
  }))

  const handlePlatformChange = (slug: string, enabled: boolean) => {
    setEnabledSlugs(prev => {
      const next = new Set(prev)
      if (enabled) next.add(slug)
      else next.delete(slug)
      return next
    })
  }

  const [createCategory, { loading }] = useMutation(CREATE_CATEGORY, {
    onCompleted: (data) => {
      toast.success('Category created successfully!')
      navigate(`/categories/${data.createCategory.id}`)
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create category')
    },
    update: (cache, { data }) => {
      if (data?.createCategory) {
        // Update the categories list cache
        const existingCategories: any = cache.readQuery({
          query: GET_CATEGORIES,
          variables: { filters: {} }
        })

        if (existingCategories) {
          cache.writeQuery({
            query: GET_CATEGORIES,
            variables: { filters: {} },
            data: {
              categories: [...existingCategories.categories, data.createCategory]
            }
          })
        } else {
        }
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Category name is required')
      return
    }

    createCategory({
      variables: {
        categoryData: {
          ...formData,
          name: formData.name.trim(),
          parent_id: formData.parent_id || undefined,
          platformSlugs: Array.from(enabledSlugs)
        }
      }
    })
  }

  const handleInputChange = (field: keyof CreateCategoryData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/categories"
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Create New Category</h1>
          <p className="text-sm text-gray-500">Add a new category to organize your products</p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Category Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div>
                <label htmlFor="parent_id" className="block text-sm font-medium text-gray-700">
                  Parent Category
                </label>
                                 <select
                   id="parent_id"
                   value={formData.parent_id?.toString() || ''}
                   onChange={(e) => handleInputChange('parent_id', e.target.value ? parseInt(e.target.value) : undefined)}
                   className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                   disabled={categoriesLoading}
                 >
                   <option value="">No parent category (top level)</option>
                  {categories.map((category: any) => (
                     <option key={category.id} value={category.id}>
                       {category.name}
                     </option>
                   ))}
                 </select>
                <p className="mt-1 text-sm text-gray-500">
                  Select a parent category to create a hierarchy, or leave empty for a top-level category
                </p>
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => handleInputChange('active', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="display_in_pos"
                    checked={formData.display_in_pos}
                    onChange={(e) => handleInputChange('display_in_pos', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="display_in_pos" className="ml-2 block text-sm text-gray-700">
                    Display in POS
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="display_in_webshop"
                    checked={formData.display_in_webshop}
                    onChange={(e) => handleInputChange('display_in_webshop', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="display_in_webshop" className="ml-2 block text-sm text-gray-700">
                    Display in Webshop
                  </label>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available on delivery platforms
                </label>
                <PlatformPicker
                  availabilities={availabilities}
                  onChange={handlePlatformChange}
                  showSelectAll
                  disabled={platformsLoading}
                  helperText="Products in this category are pushed to a platform only if both the category and the product itself have it enabled."
                />
              </div>
            </div>
          </div>

          {/* Category Information */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Category Information</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FolderIcon className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Category Setup
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      After creating this category, you'll be able to:
                    </p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Assign products to this category</li>
                      <li>Create subcategories under this category</li>
                      <li>Organize your product catalog hierarchically</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3">
            <Link
              to="/categories"
              className="btn-secondary"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="btn-primary"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Creating...
                </>
              ) : (
                'Create Category'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateCategoryPage
